import { PnlAxisRow, PnlReportAxis } from 'src/components/star-grid/pnlAxis.types';
import { PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';
import { PnlGridRow, PnlPivotModel, PnlQueryResponse, PnlScenarioEntry } from 'src/components/star-grid/starGridTypes';
import { pnlAxisColumnGroups, pnlAxisField } from 'src/components/star-grid/utils/pnlAxisColumns';
import { toNumber } from 'src/components/star-grid/utils/pnlGrid';

const axisRowMetadata = (row: PnlAxisRow, reference?: PnlGridRow): PnlGridRow => ({
  [PNL_STATEMENT_LINE_DIMENSION]: row.element ?? `axis_row_${row.index}`,
  label: row.kind === 'value' && row.label === row.element ? reference?.label ?? row.label : row.label,
  row_index: row.index,
  axis_kind: row.kind,
  axis_indent: row.indent ?? 0,
  scaling: row.scaling ?? reference?.scaling ?? null,
  variance_sign: row.variance_sign ?? reference?.variance_sign ?? null
});

const axisCells = (axis: PnlReportAxis, rowPosition: number): PnlGridRow =>
  Object.fromEntries(
    axis.columns.flatMap((column, position) =>
      column.kind === 'spacer' ? [] : [[pnlAxisField(column.index), toNumber(axis.cells[rowPosition]?.[position])]]
    )
  );

/** Keep layout rows, flat-response members, and any populated axis-only financial row. */
const isVisibleAxisRow = (row: PnlGridRow, hasFlatRow: boolean, axis: PnlReportAxis): boolean =>
  row.axis_kind !== 'value' ||
  hasFlatRow ||
  axis.columns.some((column) => column.kind !== 'spacer' && typeof row[pnlAxisField(column.index)] === 'number');

/** Financial values come exclusively from the resolved matrix, including quarters and ratios. */
export const buildPnlAxisModel = (axis: PnlReportAxis, response: PnlQueryResponse, scenarios: readonly PnlScenarioEntry[]): PnlPivotModel => {
  const display = new Map(response.rows.map((row) => [row[PNL_STATEMENT_LINE_DIMENSION], row]));
  return {
    isResolvedAxis: true,
    dimensionFields: [PNL_STATEMENT_LINE_DIMENSION],
    columnGroups: pnlAxisColumnGroups(axis.columns, scenarios),
    calculatedColumns: [],
    rows: axis.rows
      .map((row, position) => ({
        ...axisRowMetadata(row, display.get(row.element ?? '')),
        ...(row.kind === 'value' || row.kind === 'ratio' ? axisCells(axis, position) : {})
      }))
      .filter((row) => isVisibleAxisRow(row, display.has(row[PNL_STATEMENT_LINE_DIMENSION]), axis))
  };
};
